import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import TourCard from "./TourCard";

const TourGrid = ({ tours = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  if (!Array.isArray(tours) || tours.length === 0) return null;

  const totalPages = Math.ceil(tours.length / itemsPerPage);
  const currentTours = tours.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(p => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(p => p - 1);
  };

  return (
    <div className="relative">
      {/* Places Grid */}
      <motion.div
        layout
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
      >
        <AnimatePresence mode="popLayout">
          {currentTours.map(
            (tour, index) =>
              tour?.id && (
                <motion.div
                  key={tour.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{
                    scale: 1.03,
                    rotateX: 2,
                    rotateY: -2,
                  }}
                  className="relative group perspective-1000 h-full"
                >
                  {/* Glow Layer */}
                  <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-r from-orange-main/25 to-amber-400/25 blur-xl opacity-0 group-hover:opacity-100 transition duration-500" />

                  <div className="relative rounded-[2rem] overflow-hidden h-full shadow-[0_4px_20px_rgb(0,0,0,0.05)] bg-white">
                    <TourCard tour={tour} />
                  </div>
                </motion.div>
              )
          )}
        </AnimatePresence>
      </motion.div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-16 border-t border-gray-200/60 pt-8">
          <button 
            onClick={prevPage}
            disabled={currentPage === 1}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
              currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-800 shadow-sm hover:bg-orange-main hover:text-white border border-gray-200 hover:border-transparent'
            }`}
          >
            <FaChevronLeft className="text-xs -ml-0.5" />
          </button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  currentPage === i + 1 
                    ? 'bg-orange-main w-6' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button 
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
              currentPage === totalPages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-800 shadow-sm hover:bg-orange-main hover:text-white border border-gray-200 hover:border-transparent'
            }`}
          >
            <FaChevronRight className="text-xs -mr-0.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TourGrid;
